import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Divider,
  IconButton,
  useMediaQuery,
  Paper,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  LocationOn,
} from "@mui/icons-material";
import theme from "../theme/Theme";
import WaiverForm from "../../assets/Waiver-form.pdf";
import CloseIcon from "@mui/icons-material/Close";
import { getVideoUrl } from "../../apis/LaneCageApi";

const Footer: React.FC = () => {
  // const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [videoUrl, setVideoUrl] = useState("");
  const [openModal, setOpenModal] = useState(false);

  const handleOpenVideoModal = async () => {
    try {
      const response = await getVideoUrl();
      setVideoUrl(response.url);
      setOpenModal(true);
    } catch (error: any) {
      console.error("Error fetching video URL:", error.message);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.primary.main,
        color: "white",
        mt: "auto",
        pt: 6,
        pb: 3,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={isMobile ? 3 : 4}>
          {/* Company Info */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
              The Bat Cage
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Your premier destination for booking sports facilities. We provide
              easy access to lanes and cages for your sporting needs.
            </Typography>
            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
              <IconButton size="small" sx={{ color: "white", mr: 1, p: 0 }}>
                <LocationIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2">
                50A Neptune Crescent Unit 1, Dartmouth, NS B2Y 0B6
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
              <IconButton size="small" sx={{ color: "white", mr: 1, p: 0 }}>
                <PhoneIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2">+1 (902) 718-7822</Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <IconButton size="small" sx={{ color: "white", mr: 1, p: 0 }}>
                <EmailIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2">
                <Link
                  href="mailto:info@thebatcage.ca"
                  color="inherit"
                  underline="none"
                >
                  info@thebatcage.ca
                </Link>
              </Typography>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
              Quick Links
            </Typography>
            <Link
              href="/"
              color="inherit"
              underline="hover"
              display="block"
              sx={{ mb: 1 }}
            >
              Home
            </Link>
            <Link
              href="about"
              color="inherit"
              underline="hover"
              display="block"
              sx={{ mb: 1 }}
            >
              About Us
            </Link>
            <Link
              color="inherit"
              underline="hover"
              display="block"
              sx={{ mb: 1 }}
              onClick={handleOpenVideoModal}
            >
              Take a Tour
            </Link>
            <Link
              href="listings"
              color="inherit"
              underline="hover"
              display="block"
              sx={{ mb: 1 }}
            >
              Book a Session
            </Link>
          </Grid>

          {/* User Resources */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
              User Resources
            </Typography>
            <Link
              color="inherit"
              download
              href={WaiverForm}
              underline="hover"
              display="block"
              sx={{ mb: 1 }}
            >
              Download Waiver Form
            </Link>
          </Grid>

          {/* Newsletter */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
              Stay Connected
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Follow us on social media for updates and special offers.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <IconButton
                aria-label="Facebook"
                href="https://www.facebook.com/p/The-Bat-Cage-61558768148753/"
                target="_blank"
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                <FacebookIcon />
              </IconButton>
              <IconButton
                aria-label="Instagram"
                href="https://www.instagram.com/the_bat_cage/?hl=en"
                target="_blank"
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                <InstagramIcon />
              </IconButton>
              <IconButton
                aria-label="Google"
                href="https://g.co/kgs/5qAdqgg"
                target="_blank"
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                <LocationOn />
              </IconButton>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "rgba(255,255,255,0.1)",
                borderRadius: 2,
                color: "white",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                Need urgent help?
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                Call our support: +1(902) 718-7822
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, backgroundColor: "rgba(255,255,255,0.2)" }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "center" : "flex-start",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <Typography variant="body2" sx={{ mb: isMobile ? 2 : 0 }}>
            © {currentYear} The Bat Cage. All rights reserved.
          </Typography>
        </Box>

        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <IconButton
            onClick={scrollToTop}
            sx={{
              backgroundColor: theme.palette.secondary.main,
              color: "white",
              "&:hover": {
                backgroundColor: theme.palette.secondary.dark,
              },
              width: 40,
              height: 40,
            }}
            aria-label="Scroll to top"
          >
            <KeyboardArrowUpIcon />
          </IconButton>
        </Box>
      </Container>
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
      >
        <IconButton
          onClick={handleCloseModal}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            zIndex: 2,
            backgroundColor: "rgba(0,0,0,0.3)",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.5)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ padding: 0, overflow: "hidden", bgcolor: "#000" }}>
          {videoUrl && (
            <video
              src={videoUrl}
              style={{ width: "100%", height: "auto" }}
              controls
              autoPlay
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Footer;
